import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, Building2, Users, CalendarCheck, DollarSign, CalendarX,
  Menu, LogOut, Gem, ChevronRight,
} from 'lucide-react';
import Dashboard from './components/Pages/Dashboard';
import Departments from './components/Pages/Departments';
import Employees from './components/Pages/Employees';
import AttendanceScreen from './components/Pages/AttendanceScreen';
import PayrollScreen from './components/Pages/PayrollScreen';
import LeaveScreen from './components/Pages/LeaveScreen';
import Toast from './components/UI/Toast';
import HR_PAGE_STYLES from './styles/globalsHR';
import { getStoredUser, hrApi, setStoredOrgId } from '../api';
import { getOrgId, getUserRole, canWrite } from './utils/auth';
import ModuleGoalsDashboard from '../Goals/ModuleGoalsDashboard';

function asText(value) {
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  if (value && typeof value === 'object') {
    return String(
      value.display_name ||
      value.name ||
      value.username ||
      value.role ||
      value.user_role ||
      value.message ||
      value.code ||
      ''
    ).trim();
  }
  return '';
}

const HR_STYLES = `
  .hr-layout { display: flex; height: 100vh; overflow: hidden; font-family: 'Inter', sans-serif; }
  .hr-sidebar {
    width: 260px; min-width: 260px; background: linear-gradient(180deg,#1e1b4b 0%,#312e81 50%,#1e293b 100%);
    color: white; display: flex; flex-direction: column; transition: transform .3s ease; z-index: 20;
    box-shadow: 4px 0 24px rgba(0,0,0,.3);
  }
  .hr-main { flex: 1; display: flex; flex-direction: column; overflow: hidden; background: #f8fafc; }
  .hr-header {
    height: 64px; background: white; border-bottom: 1px solid #e2e8f0;
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 24px; box-shadow: 0 1px 4px rgba(0,0,0,.06); z-index: 10; flex-shrink: 0;
  }
  .hr-content { flex: 1; overflow-y: auto; padding: 24px; background: #f8fafc; }
  .hr-content,
  .hr-content h1, .hr-content h2, .hr-content h3, .hr-content h4, .hr-content h5, .hr-content h6,
  .hr-content p, .hr-content span, .hr-content label, .hr-content small,
  .hr-content td, .hr-content th, .hr-content li, .hr-content a,
  .hr-content input, .hr-content textarea, .hr-content select {
    color: #000 !important;
  }
  .hr-content input::placeholder,
  .hr-content textarea::placeholder { color: #000 !important; opacity: 1; }
  .hr-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.4); z-index: 19; display: none; }
  .hr-overlay.visible { display: block; }
  .hr-nav-item {
    display: flex; align-items: center; gap: 12px; padding: 10px 16px; border-radius: 10px;
    cursor: pointer; transition: all .2s; border: none; background: transparent; color: #a5b4fc;
    font-size: 14px; font-weight: 500; width: 100%; text-align: left;
  }
  .hr-nav-item:hover { background: rgba(255,255,255,.08); color: white; }
  .hr-nav-item.active { background: rgba(255,255,255,.12); color: white; box-shadow: inset 0 0 0 1px rgba(255,255,255,.1); }
  @media(max-width:768px) {
    .hr-sidebar { position: fixed; height: 100%; transform: translateX(-100%); }
    .hr-sidebar.open { transform: translateX(0); }
    .hr-content { padding: 16px; }
  }
  @media(min-width:769px) { .hr-sidebar { position: relative; transform: none !important; } }
  .hr-content {
    --text-primary: #000;
    --text-secondary: #000;
    --text-muted: #000;
    --text-light: #000;
  }
  ${HR_PAGE_STYLES}
`;

export default function HRRouting({ onLogout }) {
  const [activePage, setActivePage] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [attendanceDB, setAttendanceDB] = useState({});
  const [payrollDB, setPayrollDB] = useState({});
  const [orgId, setOrgId] = useState(() => getOrgId());
  const [userRole, setUserRole] = useState(() => getUserRole());
  const [userName, setUserName] = useState('HR User');

  useEffect(() => {
    const bootstrap = async () => {
      let org = getOrgId();
      if (!org) {
        try {
          const res = await hrApi.get('/hr/me');
          org = res.data?.data?.profile?.org_id;
          if (org) {
            setStoredOrgId(org);
            setOrgId(org);
          }
        } catch (e) {
          console.error('Could not resolve org_id from /hr/me', e);
        }
      } else {
        setOrgId(org);
      }
      if (org) {
        hrApi.get(`/orgs/${org}/hr/departments`)
          .then((r) => setDepartments(r.data.data || []))
          .catch(() => {});
      }
    };
    bootstrap();
    setUserRole(getUserRole());
    try {
      const storedUser = getStoredUser();
      const resolvedName = [
        [storedUser?.first_name, storedUser?.last_name].filter(Boolean).join(' ').trim(),
        asText(storedUser?.display_name),
        asText(storedUser?.name),
        asText(storedUser?.username),
      ].find((value) => asText(value));
      if (resolvedName) {
        setUserName(resolvedName);
      }
    } catch {
      setUserName('HR User');
    }
  }, []);

  const addToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'departments', label: 'Departments', icon: Building2 },
    { id: 'employees', label: 'Employees', icon: Users },
    { id: 'attendance', label: 'Attendance', icon: CalendarCheck },
    { id: 'leave', label: 'Leave', icon: CalendarX },
    { id: 'payroll', label: 'Payroll', icon: DollarSign },
    { id: 'goals', label: 'Goals & Tasks', icon: LayoutDashboard },
  ];

  const canUserWrite = canWrite();

  const renderContent = () => {
    if (!orgId) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 256, color: '#64748b', fontSize: 14, gap: 8 }}>
          <svg style={{ width: 20, height: 20, color: '#6366f1', animation: 'spin 0.8s linear infinite' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          Initialising HR module...
        </div>
      );
    }

    switch (activePage) {
      case 'dashboard':
        return <Dashboard employees={employees} attendanceDB={attendanceDB} payrollDB={payrollDB} setActivePage={setActivePage} userRole={userRole} canWrite={canUserWrite} />;
      case 'departments':
        return <Departments departments={departments} employees={employees} userRole={userRole} canWrite={canUserWrite} />;
      case 'employees':
        return <Employees employees={employees} setEmployees={setEmployees} departments={departments} attendanceDB={attendanceDB} payrollDB={payrollDB} addToast={addToast} userRole={userRole} canWrite={canUserWrite} />;
      case 'attendance':
        return <AttendanceScreen employees={employees} departments={departments} attendanceDB={attendanceDB} setAttendanceDB={setAttendanceDB} payrollDB={payrollDB} addToast={addToast} userRole={userRole} canWrite={canUserWrite} />;
      case 'leave':
        return <LeaveScreen addToast={addToast} userRole={userRole} canWrite={canUserWrite} />;
      case 'payroll':
        return <PayrollScreen employees={employees} departments={departments} attendanceDB={attendanceDB} payrollDB={payrollDB} setPayrollDB={setPayrollDB} addToast={addToast} userRole={userRole} canWrite={canUserWrite} />;
      case 'goals':
        return <ModuleGoalsDashboard moduleName="HR" />;
      default:
        return <Dashboard employees={employees} attendanceDB={attendanceDB} payrollDB={payrollDB} setActivePage={setActivePage} userRole={userRole} canWrite={canUserWrite} />;
    }
  };

  const getPageTitle = () => navItems.find((n) => n.id === activePage)?.label || 'HR';

  return (
    <>
      <style>{HR_STYLES}</style>
      <div className="hr-layout prod-module-root">
        <div className={`hr-overlay ${sidebarOpen ? 'visible' : ''}`} onClick={() => setSidebarOpen(false)} />

        <aside className={`hr-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,.1)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, background: 'linear-gradient(135deg,#f59e0b,#d97706)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Gem size={22} color="white" />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 900, letterSpacing: '-.5px', color: 'white' }}>
                ZILVERRA<span style={{ color: '#818cf8' }}>JEWELS</span>
              </div>
              <div style={{ fontSize: 11, color: '#a5b4fc' }}>HR Module</div>
            </div>
          </div>

          <nav style={{ padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '1px', padding: '0 4px 8px' }}>
              HR Hub
            </div>
            {navItems.map((item) => (
              <button
                key={item.id}
                className={`hr-nav-item ${activePage === item.id ? 'active' : ''}`}
                onClick={() => {
                  setActivePage(item.id);
                  setSidebarOpen(false);
                }}
              >
                <item.icon size={18} />
                {item.label}
                {activePage === item.id && <ChevronRight size={14} style={{ marginLeft: 'auto' }} />}
              </button>
            ))}
          </nav>

          <div style={{ margin: '12px', padding: '12px', background: 'rgba(0,0,0,.25)', borderRadius: 10, border: '1px solid rgba(255,255,255,.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'white' }}>
                {userName?.charAt(0) || 'U'}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'white' }}>{userName}</div>
                <div style={{ fontSize: 11, color: '#a5b4fc' }}>{asText(userRole) || 'Employee'}</div>
              </div>
            </div>
          </div>
        </aside>

        <main className="hr-main">
          <header className="hr-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1a1a1a', padding: 4, display: 'flex', alignItems: 'center' }}
                onClick={() => setSidebarOpen((v) => !v)}
              >
                <Menu size={22} />
              </button>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#000', margin: 0 }}>{getPageTitle()}</h2>
            </div>
            <button
              onClick={() => window.confirm('Logout?') && onLogout?.()}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', border: '1px solid #fecaca', background: '#fff5f5', color: '#dc2626', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
            >
              <LogOut size={16} />
              Logout
            </button>
          </header>

          <div className="hr-content">
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
              {renderContent()}
            </div>
          </div>

          <div className="toast-container">
            {toasts.map((toast) => (
              <Toast key={toast.id} {...toast} onClose={() => removeToast(toast.id)} />
            ))}
          </div>
        </main>
      </div>
    </>
  );
}
